import type { CreateSeriesInput, UpdateSeriesInput } from '@financiar/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Drawer, Form, Input, InputNumber, Select, Space, Switch } from 'antd';
import { useEffect } from 'react';
import { getApiError } from '../../api/client';
import { applyServerErrors } from '../../lib/forms';
import type { Series } from '../../api/types';
import { createSeries, updateSeries } from './api';

const DOC_TYPE_OPTIONS = [
  { value: 'INVOICE', label: 'Factură' },
  { value: 'PROFORMA', label: 'Proformă' },
];
const PADDING_OPTIONS = [3, 4, 5, 6].map((n) => ({ value: n, label: String(n) }));

const NUMBERING_LOCK_TOOLTIP = 'Seria are facturi emise — proprietățile de numerotare nu mai pot fi modificate.';

interface SeriesFormValues {
  prefix: string;
  docType: 'INVOICE' | 'PROFORMA';
  startNumber: number;
  padding: number;
  yearlyReset: boolean;
  isDefault: boolean;
  isActive: boolean;
}

const CREATE_DEFAULTS: Partial<SeriesFormValues> = {
  padding: 4,
  startNumber: 1,
  yearlyReset: false,
  isDefault: false,
  isActive: true,
};

/**
 * `/serii`'s create/edit Drawer. No separate `GET /series/:id` endpoint
 * exists — `series` is the already-fetched list row passed straight from
 * SeriiListPage, not re-fetched here. `invoiceCount > 0` locks every
 * numbering-relevant field (prefix/docType/startNumber/padding/yearlyReset)
 * to mirror SeriesService.update()'s SERIES_IN_USE guard.
 */
export function SerieFormDrawer({
  open,
  firmaId,
  series,
  onClose,
}: {
  open: boolean;
  firmaId: string;
  series: Series | null;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SeriesFormValues>();
  const isEdit = series !== null;
  const numberingLocked = series !== null && series.invoiceCount > 0;

  useEffect(() => {
    if (!open) return;
    if (series) {
      form.setFieldsValue({
        prefix: series.prefix,
        docType: series.docType,
        startNumber: series.startNumber,
        padding: series.padding,
        yearlyReset: series.yearlyReset,
        isDefault: series.isDefault,
        isActive: series.isActive,
      });
    } else {
      form.resetFields();
      form.setFieldsValue(CREATE_DEFAULTS);
    }
  }, [open, series, form]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['series', firmaId] });

  const createMutation = useMutation({
    mutationFn: (values: CreateSeriesInput) => createSeries(firmaId, values),
    onSuccess: () => {
      message.success('Seria a fost creată.');
      invalidate();
      onClose();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Seria nu a putut fi creată.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: UpdateSeriesInput) => updateSeries(series!.id, values),
    onSuccess: () => {
      message.success('Seria a fost actualizată.');
      invalidate();
      onClose();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Seria nu a putut fi actualizată.');
    },
  });

  function handleFinish(values: SeriesFormValues) {
    const payload = {
      prefix: values.prefix.trim().toUpperCase(),
      docType: values.docType,
      startNumber: values.startNumber,
      padding: values.padding,
      yearlyReset: values.yearlyReset,
      isDefault: values.isDefault,
      isActive: values.isActive,
    };
    if (isEdit) updateMutation.mutate(payload);
    else createMutation.mutate(payload as CreateSeriesInput);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Drawer
      title={isEdit ? 'Editează seria' : 'Adaugă serie'}
      width="clamp(560px, 40vw, 960px)"
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Închide</Button>
          <Button type="primary" onClick={() => form.submit()} loading={isSaving}>
            Salvează
          </Button>
        </Space>
      }
    >
      <Form<SeriesFormValues> form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="prefix"
          label="Prefix"
          tooltip={numberingLocked ? NUMBERING_LOCK_TOOLTIP : undefined}
          normalize={(value: string) => value?.toUpperCase()}
          rules={[
            { required: true, message: 'Prefixul este obligatoriu.' },
            {
              pattern: /^[A-Z][A-Z0-9]{0,11}$/,
              message: 'Prefixul trebuie să înceapă cu o literă mare și să conțină doar litere mari și cifre (1-12 caractere).',
            },
          ]}
        >
          <Input disabled={numberingLocked} />
        </Form.Item>
        <Form.Item
          name="docType"
          label="Tip document"
          tooltip={numberingLocked ? NUMBERING_LOCK_TOOLTIP : undefined}
          rules={[{ required: true, message: 'Tipul documentului este obligatoriu.' }]}
        >
          <Select options={DOC_TYPE_OPTIONS} disabled={numberingLocked} />
        </Form.Item>
        <Form.Item
          name="startNumber"
          label="Număr start"
          tooltip={numberingLocked ? NUMBERING_LOCK_TOOLTIP : undefined}
          rules={[{ required: true, message: 'Numărul de start este obligatoriu.' }]}
        >
          <InputNumber min={1} precision={0} style={{ width: '100%' }} disabled={numberingLocked} />
        </Form.Item>
        <Form.Item
          name="padding"
          label="Padding"
          tooltip={numberingLocked ? NUMBERING_LOCK_TOOLTIP : undefined}
          rules={[{ required: true, message: 'Padding-ul este obligatoriu.' }]}
        >
          <Select options={PADDING_OPTIONS} disabled={numberingLocked} />
        </Form.Item>
        <Form.Item
          name="yearlyReset"
          label="Resetare anuală"
          tooltip={numberingLocked ? NUMBERING_LOCK_TOOLTIP : undefined}
          valuePropName="checked"
        >
          <Switch disabled={numberingLocked} />
        </Form.Item>
        <Form.Item name="isDefault" label="Serie implicită" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="isActive" label="Activă" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
